import { API_ORIGIN, apiUrl } from "./apiOrigin";
// src/DeliveryActivityReview.js
// =============================================================================
// Head office → Deliveries → Activity Review (2026-10)
//
// Pick a pharmacy and a date range; four views of that branch's deliveries:
//   1. Patients delivered to — per month: visits (distinct delivery rounds)
//      and items (prescriptions), with totals.
//   2. Failed deliveries — the same, for unsuccessful attempts.
//   3. Drivers — per month: patients delivered to and items, for successful
//      and failed deliveries, with grand totals.
//   4. Heat map — where deliveries went; hover an area for its postcodes.
//   5. Efficiency — avoidable (bunched) trips, gaps between deliveries, each
//      patient's rhythm, and every branch side by side (DeliveryEfficiency.js).
// Every column sorts; grand totals sit at the foot of every table. The search
// box filters patients (name or postcode) and the map.
// Backend: delivery_activity_review.js (/api/headoffice/delivery-review/report).
// =============================================================================
import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Circle, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import * as XLSX from "xlsx";
import { displayName as maskName } from "./nameMask";
import DeliveryEfficiency from "./DeliveryEfficiency";
import DeliveryActivity from "./DeliveryActivity"; // "Activity" tab: the group-wide bar chart (2026-10)

const API = apiUrl("/api/headoffice/delivery-review");
const iso = d => d.toISOString().slice(0, 10);
const monthLabel = ym => new Date(+ym.slice(0, 4), +ym.slice(5, 7) - 1, 1).toLocaleString("en-GB", {
  month: "short",
  year: "2-digit"
});
const num = n => (n || 0).toLocaleString("en-GB");
const th = {
  position: "sticky",
  top: 0,
  background: "#F5F5F5",
  borderBottom: "1px solid #D9D9D9",
  padding: "6px 8px",
  fontSize: 12,
  fontWeight: 600,
  textAlign: "right",
  cursor: "pointer",
  userSelect: "none",
  whiteSpace: "nowrap",
  zIndex: 1
};
const td = {
  borderBottom: "1px solid #EDF2F7",
  padding: "5px 8px",
  fontSize: 13,
  textAlign: "right",
  whiteSpace: "nowrap"
};
const foot = {
  ...td,
  fontWeight: 700,
  background: "#FAFAFA",
  borderTop: "2px solid #D9D9D9"
};
const btn = on => ({
  padding: "6px 12px",
  borderRadius: 6,
  border: on ? "none" : "1px solid #D9D9D9",
  cursor: "pointer",
  background: on ? "#0E8CFF" : "#fff",
  color: on ? "#fff" : "#262626",
  fontSize: 13,
  fontWeight: on ? 600 : 400
});

// One sheet, headed with what it is, for the per-tab Export buttons.
function exportSheet({
  title,
  meta,
  header,
  body,
  footer,
  fileStem
}) {
  const aoa = [[title], ...(meta || []).map(m => [m]), [], header, ...body, ...(footer ? [footer] : [])];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = header.map((h, i) => ({
    wch: i === 0 ? 28 : Math.max(10, String(h).length + 2)
  }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, title.slice(0, 31).replace(/[\\/?*[\]:]/g, " "));
  XLSX.writeFile(wb, `${fileStem.replace(/[^a-z0-9]+/gi, "_")}.xlsx`);
}
const exportBtn = {
  padding: "5px 10px",
  borderRadius: 6,
  border: "1px solid #D9D9D9",
  background: "#fff",
  color: "#262626",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 600
};
function useSort(defaultKey) {
  const [sort, setSort] = useState({
    key: defaultKey,
    dir: "desc"
  });
  const toggle = key => setSort(s => s.key === key ? {
    key,
    dir: s.dir === "asc" ? "desc" : "asc"
  } : {
    key,
    dir: ["name", "postcode"].includes(key) ? "asc" : "desc"
  });
  const arrow = key => sort.key === key ? sort.dir === "asc" ? " ▲" : " ▼" : "";
  const apply = (rows, get) => [...rows].sort((a, b) => {
    const va = get(a, sort.key),
      vb = get(b, sort.key);
    const c = typeof va === "string" || typeof vb === "string" ? String(va || "").localeCompare(String(vb || "")) : (va || 0) - (vb || 0);
    return sort.dir === "asc" ? c : -c;
  });
  return {
    sort,
    toggle,
    arrow,
    apply
  };
}

// ---------------------------------------------------------------------------
// Patients table (delivered or failed)
// ---------------------------------------------------------------------------
function PatientTable({
  rows,
  totals,
  months,
  masked,
  label,
  exportInfo
}) {
  const {
    toggle,
    arrow,
    apply
  } = useSort("totalVisits");
  const get = (r, key) => {
    if (key === "name" || key === "postcode") return r[key];
    if (key === "totalVisits" || key === "totalItems") return r[key];
    const [m, f] = key.split("|");
    return r.months[m]?.[f];
  };
  const sorted = apply(rows, get);
  // Footer: totals of the rows shown (a search narrows them).
  const shown = {
    months: Object.fromEntries(months.map(m => [m, {
      visits: rows.reduce((t, r) => t + r.months[m].visits, 0),
      items: rows.reduce((t, r) => t + r.months[m].items, 0)
    }])),
    totalVisits: rows.reduce((t, r) => t + r.totalVisits, 0),
    totalItems: rows.reduce((t, r) => t + r.totalItems, 0)
  };
  const filtered = totals && rows.length !== totals.patients;
  const doExport = () => exportSheet({
    title: exportInfo.title,
    meta: exportInfo.meta,
    fileStem: exportInfo.fileStem,
    header: ["Patient", "Postcode", ...months.flatMap(m => [`${monthLabel(m)} visits`, `${monthLabel(m)} items`]), "Total visits", "Total items"],
    body: sorted.map(r => [maskName(r.name, masked), r.postcode, ...months.flatMap(m => [r.months[m].visits, r.months[m].items]), r.totalVisits, r.totalItems]),
    footer: [filtered ? `Total (${rows.length} shown of ${totals.patients})` : `Total — ${rows.length} patients`, "", ...months.flatMap(m => [shown.months[m].visits, shown.months[m].items]), shown.totalVisits, shown.totalItems]
  });
  return <>
    {exportInfo && <div style={{
      display: "flex",
      justifyContent: "flex-end",
      marginBottom: 6
    }}>
      <button style={exportBtn} onClick={doExport} disabled={!rows.length} title="This table as shown - same sort, search and totals">Export to Excel</button></div>}
    <div style={{
      overflow: "auto",
      maxHeight: "62vh",
      border: "1px solid #F0F0F0",
      borderRadius: 8
    }}>
      <table style={{
        borderCollapse: "collapse",
        width: "100%"
      }}>
        <thead>
          <tr>
            <th style={{
              ...th,
              textAlign: "left",
              left: 0,
              zIndex: 2
            }} rowSpan={2} onClick={() => toggle("name")}>Patient{arrow("name")}</th>
            <th style={{
              ...th,
              textAlign: "left"
            }} rowSpan={2} onClick={() => toggle("postcode")}>Postcode{arrow("postcode")}</th>
            {months.map(m => <th key={m} colSpan={2} style={{
              ...th,
              textAlign: "center",
              cursor: "default",
              borderLeft: "1px solid #D9D9D9"
            }}>{monthLabel(m)}</th>)}
            <th colSpan={2} style={{
              ...th,
              textAlign: "center",
              cursor: "default",
              borderLeft: "2px solid #8C8C8C"
            }}>Total</th>
          </tr>
          <tr>
            {months.map(m => <React.Fragment key={m}>
                <th style={{
                ...th,
                top: 28,
                borderLeft: "1px solid #D9D9D9"
              }} title="Visits: distinct delivery rounds" onClick={() => toggle(`${m}|visits`)}>Visits{arrow(`${m}|visits`)}</th>
                <th style={{
                ...th,
                top: 28
              }} title="Items: prescriptions delivered" onClick={() => toggle(`${m}|items`)}>Items{arrow(`${m}|items`)}</th>
              </React.Fragment>)}
            <th style={{
              ...th,
              top: 28,
              borderLeft: "2px solid #8C8C8C"
            }} onClick={() => toggle("totalVisits")}>Visits{arrow("totalVisits")}</th>
            <th style={{
              ...th,
              top: 28
            }} onClick={() => toggle("totalItems")}>Items{arrow("totalItems")}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(r => <tr key={r.patientId}>
              <td style={{
              ...td,
              textAlign: "left"
            }}>{maskName(r.name, masked)}</td>
              <td style={{
              ...td,
              textAlign: "left"
            }}>{r.postcode || <span style={{
                color: "#8C8C8C"
              }}>—</span>}</td>
              {months.map(m => <React.Fragment key={m}>
                  <td style={{
                ...td,
                borderLeft: "1px solid #EDF2F7",
                color: r.months[m].visits ? undefined : "#D9D9D9"
              }}>{r.months[m].visits}</td>
                  <td style={{
                ...td,
                color: r.months[m].items ? undefined : "#D9D9D9"
              }}>{r.months[m].items}</td>
                </React.Fragment>)}
              <td style={{
              ...td,
              borderLeft: "2px solid #F0F0F0",
              fontWeight: 600
            }}>{r.totalVisits}</td>
              <td style={{
              ...td,
              fontWeight: 600
            }}>{r.totalItems}</td>
            </tr>)}
          {!sorted.length && <tr><td colSpan={months.length * 2 + 4} style={{
              ...td,
              textAlign: "center",
              color: "#5D5D5D"
            }}>No {label} in this range.</td></tr>}
        </tbody>
        <tfoot>
          <tr>
            <td style={{
              ...foot,
              textAlign: "left"
            }} colSpan={2}>{filtered ? `Total (${rows.length} shown of ${totals.patients})` : `Total — ${num(rows.length)} patient${rows.length === 1 ? "" : "s"}`}</td>
            {months.map(m => <React.Fragment key={m}>
                <td style={{
                ...foot,
                borderLeft: "1px solid #D9D9D9"
              }}>{num(shown.months[m].visits)}</td>
                <td style={foot}>{num(shown.months[m].items)}</td>
              </React.Fragment>)}
            <td style={{
              ...foot,
              borderLeft: "2px solid #8C8C8C"
            }}>{num(shown.totalVisits)}</td>
            <td style={foot}>{num(shown.totalItems)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
    </>;
}

// ---------------------------------------------------------------------------
// Drivers table (delivered or failed)
// ---------------------------------------------------------------------------
function DriverTable({
  rows,
  totals,
  months,
  masked,
  title,
  exportInfo
}) {
  const {
    toggle,
    arrow,
    apply
  } = useSort("totalPatients");
  const get = (r, key) => {
    if (key === "name" || key === "totalPatients" || key === "totalItems") return r[key];
    const [m, f] = key.split("|");
    return r.months[m]?.[f];
  };
  const sorted = apply(rows, get);
  const doExport = () => exportSheet({
    title: `${exportInfo.title} — ${title}`,
    meta: exportInfo.meta,
    fileStem: `${exportInfo.fileStem}_${title}`,
    header: ["Driver", ...months.flatMap(m => [`${monthLabel(m)} patients`, `${monthLabel(m)} items`]), "Total patients", "Total items"],
    body: sorted.map(r => [maskName(r.name, masked), ...months.flatMap(m => [r.months[m].patients, r.months[m].items]), r.totalPatients, r.totalItems]),
    footer: ["Grand total (each patient counted once)", ...months.flatMap(m => [totals?.months?.[m]?.patients || 0, totals?.months?.[m]?.items || 0]), totals?.totalPatients || 0, totals?.totalItems || 0]
  });
  return <div style={{
    marginBottom: 18
  }}>
      <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      margin: "10px 0 6px"
    }}>
        <h4 style={{
        margin: 0
      }}>{title}</h4>
        {exportInfo && <button style={exportBtn} onClick={doExport} disabled={!rows.length} title="This table as shown - same sort and grand totals">Export to Excel</button>}
      </div>
      <div style={{
      overflow: "auto",
      border: "1px solid #F0F0F0",
      borderRadius: 8
    }}>
        <table style={{
        borderCollapse: "collapse",
        width: "100%"
      }}>
          <thead>
            <tr>
              <th style={{
              ...th,
              textAlign: "left"
            }} rowSpan={2} onClick={() => toggle("name")}>Driver{arrow("name")}</th>
              {months.map(m => <th key={m} colSpan={2} style={{
              ...th,
              textAlign: "center",
              cursor: "default",
              borderLeft: "1px solid #D9D9D9"
            }}>{monthLabel(m)}</th>)}
              <th colSpan={2} style={{
              ...th,
              textAlign: "center",
              cursor: "default",
              borderLeft: "2px solid #8C8C8C"
            }}>Total</th>
            </tr>
            <tr>
              {months.map(m => <React.Fragment key={m}>
                  <th style={{
                ...th,
                top: 28,
                borderLeft: "1px solid #D9D9D9"
              }} title="Different patients" onClick={() => toggle(`${m}|patients`)}>Patients{arrow(`${m}|patients`)}</th>
                  <th style={{
                ...th,
                top: 28
              }} title="Prescriptions (packets)" onClick={() => toggle(`${m}|items`)}>Items{arrow(`${m}|items`)}</th>
                </React.Fragment>)}
              <th style={{
              ...th,
              top: 28,
              borderLeft: "2px solid #8C8C8C"
            }} onClick={() => toggle("totalPatients")}>Patients{arrow("totalPatients")}</th>
              <th style={{
              ...th,
              top: 28
            }} onClick={() => toggle("totalItems")}>Items{arrow("totalItems")}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => <tr key={r.driverId}>
                <td style={{
              ...td,
              textAlign: "left"
            }}>{maskName(r.name, masked)}</td>
                {months.map(m => <React.Fragment key={m}>
                    <td style={{
                ...td,
                borderLeft: "1px solid #EDF2F7",
                color: r.months[m].patients ? undefined : "#D9D9D9"
              }}>{r.months[m].patients}</td>
                    <td style={{
                ...td,
                color: r.months[m].items ? undefined : "#D9D9D9"
              }}>{r.months[m].items}</td>
                  </React.Fragment>)}
                <td style={{
              ...td,
              borderLeft: "2px solid #F0F0F0",
              fontWeight: 600
            }}>{r.totalPatients}</td>
                <td style={{
              ...td,
              fontWeight: 600
            }}>{r.totalItems}</td>
              </tr>)}
            {!sorted.length && <tr><td colSpan={months.length * 2 + 3} style={{
              ...td,
              textAlign: "center",
              color: "#5D5D5D"
            }}>None in this range.</td></tr>}
          </tbody>
          <tfoot>
            <tr>
              <td style={{
              ...foot,
              textAlign: "left"
            }} title="Patients are counted once even if more than one driver visited them">Grand total</td>
              {months.map(m => <React.Fragment key={m}>
                  <td style={{
                ...foot,
                borderLeft: "1px solid #D9D9D9"
              }}>{num(totals?.months?.[m]?.patients)}</td>
                  <td style={foot}>{num(totals?.months?.[m]?.items)}</td>
                </React.Fragment>)}
              <td style={{
              ...foot,
              borderLeft: "2px solid #8C8C8C"
            }}>{num(totals?.totalPatients)}</td>
              <td style={foot}>{num(totals?.totalItems)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div style={{
      fontSize: 11,
      color: "#5D5D5D",
      marginTop: 4
    }}>Grand totals count each patient once, so they can be lower than the column sums when more than one driver visited the same patient.</div>
    </div>;
}

// ---------------------------------------------------------------------------
// Heat map
// ---------------------------------------------------------------------------
// Fit the map to where most deliveries are: the outermost 2% each side is
// ignored so one stray postcode can't zoom the map out to the whole country.
function FitBounds({
  points
}) {
  const map = useMap();
  const key = points.length;
  useEffect(() => {
    if (!points.length) return;
    const q = (arr, f) => {
      const s = [...arr].sort((a, b) => a - b);
      return s[Math.min(s.length - 1, Math.max(0, Math.round(f * (s.length - 1))))];
    };
    const lats = points.map(p => p.lat),
      lngs = points.map(p => p.lng);
    const trim = points.length >= 20 ? 0.02 : 0;
    map.fitBounds([[q(lats, trim), q(lngs, trim)], [q(lats, 1 - trim), q(lngs, 1 - trim)]], {
      padding: [30, 30],
      maxZoom: 15
    });
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}
const heat = t => {
  // 0..1 -> yellow → orange → red → dark red
  const stops = [[255, 237, 160], [254, 178, 76], [240, 59, 32], [189, 0, 38]];
  const x = Math.min(0.999, Math.max(0, t)) * (stops.length - 1),
    i = Math.floor(x),
    f = x - i;
  const c = stops[i].map((v, k) => Math.round(v + (stops[i + 1][k] - v) * f));
  return `rgb(${c.join(",")})`;
};
function HeatMap({
  points,
  kind,
  exportInfo,
  masked
}) {
  // Every delivery is placed at its POSTCODE's centre (postcodes.io, in bulk),
  // so the map shows areas rather than front doors, and a patient record whose
  // stored location is wrong or defaulted can't skew it. The stored location
  // is only used if the postcode can't be found.
  const [geo, setGeo] = useState({});
  const [geoBusy, setGeoBusy] = useState(false);
  useEffect(() => {
    const wanted = [...new Set(points.map(p => p.postcode).filter(pc => pc && !(pc in geo)))];
    if (!wanted.length) return;
    let off = false;
    setGeoBusy(true);
    (async () => {
      const found = {};
      for (let i = 0; i < wanted.length; i += 100) {
        const batch = wanted.slice(i, i + 100);
        try {
          const r = await fetch("https://api.postcodes.io/postcodes", {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              postcodes: batch
            })
          });
          const d = await r.json();
          for (const x of d.result || []) found[x.query] = x.result ? {
            lat: x.result.latitude,
            lng: x.result.longitude
          } : null;
        } catch {
          for (const pc of batch) found[pc] = null;
        }
      }
      if (!off) {
        setGeo(g => ({
          ...g,
          ...found
        }));
        setGeoBusy(false);
      }
    })();
    return () => {
      off = true;
    };
  }, [points]); // eslint-disable-line react-hooks/exhaustive-deps

  // One spot per postcode.
  const spots = useMemo(() => {
    const by = new Map();
    let unplaced = 0;
    for (const p of points) {
      const count = kind === "failed" ? p.failed : kind === "both" ? p.success + p.failed : p.success;
      if (!count) continue;
      const loc = p.postcode && geo[p.postcode] || (p.lat != null && p.lng != null ? {
        lat: p.lat,
        lng: p.lng
      } : null);
      if (!loc) {
        unplaced += count;
        continue;
      }
      const key = p.postcode || `${loc.lat.toFixed(4)},${loc.lng.toFixed(4)}`;
      const s = by.get(key) || {
        key,
        postcode: p.postcode || "(no postcode)",
        lat: loc.lat,
        lng: loc.lng,
        count: 0,
        patients: 0
      };
      s.count += count;
      s.patients += 1;
      by.set(key, s);
    }
    const list = [...by.values()].sort((a, b) => a.count - b.count); // busiest drawn last, on top
    // Colour scale against the 95th percentile, so one very busy postcode
    // doesn't wash every other spot out to pale yellow.
    const counts = list.map(s => s.count).sort((a, b) => a - b);
    const p95 = counts.length ? counts[Math.min(counts.length - 1, Math.floor(0.95 * (counts.length - 1)))] : 1;
    return {
      list,
      unplaced,
      scale: Math.max(1, p95),
      max: counts.length ? counts[counts.length - 1] : 0,
      total: list.reduce((t, s) => t + s.count, 0)
    };
  }, [points, geo, kind]);

  // Export: one row per postcode, busiest first.
  const doExport = () => {
    const byPc = new Map();
    for (const p of points) {
      const pc = p.postcode || "(no postcode)";
      const r = byPc.get(pc) || {
        pc,
        sector: p.postcode ? p.postcode.slice(0, -2) : "",
        patients: 0,
        success: 0,
        failed: 0,
        si: 0,
        fi: 0,
        located: false
      };
      r.patients += 1;
      r.success += p.success;
      r.failed += p.failed;
      r.si += p.successItems || 0;
      r.fi += p.failedItems || 0;
      if (geo[p.postcode] || p.lat != null && p.lng != null) r.located = true;
      byPc.set(pc, r);
    }
    const key = r => kind === "failed" ? r.failed : kind === "both" ? r.success + r.failed : r.success;
    const rows = [...byPc.values()].filter(r => key(r) > 0).sort((a, b) => key(b) - key(a));
    exportSheet({
      title: `${exportInfo.title} — map (${kind === "both" ? "delivered and failed" : kind === "failed" ? "failed" : "delivered"})`,
      meta: exportInfo.meta,
      fileStem: `${exportInfo.fileStem}_map`,
      header: ["Postcode", "Sector", "Patients", "Delivered visits", "Delivered items", "Failed visits", "Failed items", "On map"],
      body: rows.map(r => [r.pc, r.sector, r.patients, r.success, r.si, r.failed, r.fi, r.located ? "yes" : "no location"]),
      footer: ["Total", "", rows.reduce((t, r) => t + r.patients, 0), rows.reduce((t, r) => t + r.success, 0), rows.reduce((t, r) => t + r.si, 0), rows.reduce((t, r) => t + r.failed, 0), rows.reduce((t, r) => t + r.fi, 0), ""]
    });
  };
  const word = kind === "failed" ? "failed visit" : "visit";
  return <div>
      {exportInfo && <div style={{
      display: "flex",
      justifyContent: "flex-end",
      marginBottom: 6
    }}>
        <button style={exportBtn} onClick={doExport} disabled={!points.length} title="The postcodes behind the map, with their counts">Export to Excel</button></div>}
      <div style={{
      height: "62vh",
      borderRadius: 8,
      overflow: "hidden",
      border: "1px solid #F0F0F0",
      position: "relative"
    }}>
        <MapContainer center={[52.6, -1.6]} zoom={11} style={{
        height: "100%",
        width: "100%"
      }} scrollWheelZoom preferCanvas>
          <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <FitBounds points={spots.list} />
          {spots.list.map(s => {
          const t = Math.min(1, s.count / spots.scale);
          // Radius in metres, so spots keep their real size as you zoom.
          return <Circle key={s.key} center={[s.lat, s.lng]} radius={50 + Math.sqrt(t) * 110} pathOptions={{
            stroke: false,
            fillColor: heat(t),
            fillOpacity: 0.35 + t * 0.45
          }}>
                <Tooltip direction="top" sticky opacity={0.95}>
                  <b>{s.postcode}</b> — {s.count} {word}{s.count === 1 ? "" : "s"}{s.patients > 1 ? ` · ${s.patients} patients` : ""}
                </Tooltip>
              </Circle>;
        })}
        </MapContainer>
        {geoBusy && <div style={{
        position: "absolute",
        top: 10,
        right: 10,
        zIndex: 500,
        background: "#fff",
        padding: "4px 10px",
        borderRadius: 6,
        fontSize: 12,
        boxShadow: "0 1px 4px rgba(0,0,0,0.2)"
      }}>Placing postcodes…</div>}
      </div>
      <div style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 12,
      color: "#5D5D5D",
      marginTop: 6,
      flexWrap: "wrap"
    }}>
        <span>Fewer</span>
        <span style={{
        width: 160,
        height: 10,
        borderRadius: 5,
        background: `linear-gradient(90deg, ${heat(0)}, ${heat(0.4)}, ${heat(0.75)}, ${heat(1)})`
      }} />
        <span>More ({spots.scale}+ {word}s per postcode)</span>
        <span>· {num(spots.list.length)} postcodes, {num(spots.total)} {word}s · busiest postcode: {num(spots.max)} · hover a spot for its postcode</span>
        {spots.unplaced > 0 && <span style={{
        color: "#AD4E00"
      }}>· {spots.unplaced} {word}(s) have no findable postcode or location and are not shown.</span>}
      </div>
    </div>;
}

// ---------------------------------------------------------------------------
export default function DeliveryActivityReview({
  pharmacies = [],
  masked = false,
  token
}) {
  const list = pharmacies.length ? pharmacies : (() => {
    try {
      return JSON.parse(localStorage.getItem("members") || "[]");
    } catch {
      return [];
    }
  })();
  const sortedList = [...list].filter(p => p.pharmacyId).sort((a, b) => String(a.pharmacyName).localeCompare(String(b.pharmacyName)));
  const today = new Date();
  const [pharmacyId, setPharmacyId] = useState("");
  const [from, setFrom] = useState(iso(new Date(today.getFullYear(), today.getMonth() - 2, 1, 12)));
  const [to, setTo] = useState(iso(new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12)));
  const [tab, setTab] = useState("patients");
  const [mapKind, setMapKind] = useState("success");
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!pharmacyId || !from || !to) {
      setData(null);
      return;
    }
    let off = false;
    setBusy(true);
    setError("");
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/report`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`
          },
          body: JSON.stringify({
            dbName: pharmacyId,
            from,
            to
          })
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
        if (!off) setData(d);
      } catch (e) {
        if (!off) {
          setError(e.message);
          setData(null);
        }
      } finally {
        if (!off) setBusy(false);
      }
    }, 300);
    return () => {
      off = true;
      clearTimeout(t);
    };
  }, [pharmacyId, from, to]);
  const q = search.trim().toLowerCase();
  const qPc = q.replace(/\s+/g, "");
  const match = r => !q || String(r.name || "").toLowerCase().includes(q) || String(r.postcode || "").toLowerCase().replace(/\s+/g, "").includes(qPc);
  const months = data?.months || [];
  const exportInfo = title => data && {
    title,
    meta: [`Pharmacy: ${maskName(data.pharmacyName, masked)}`, `Dates: ${from} to ${to}`, ...(search ? [`Search: "${search}"`] : []), `Exported: ${new Date().toLocaleString("en-GB")}`],
    fileStem: `${title}_${data.pharmacyName || "pharmacy"}_${from}_to_${to}`
  };
  const exportTab = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    const pSheet = rows => rows.filter(match).map(r => {
      const o = {
        Patient: r.name,
        Postcode: r.postcode
      };
      for (const m of months) {
        o[`${monthLabel(m)} visits`] = r.months[m].visits;
        o[`${monthLabel(m)} items`] = r.months[m].items;
      }
      o["Total visits"] = r.totalVisits;
      o["Total items"] = r.totalItems;
      return o;
    });
    const dSheet = rows => rows.map(r => {
      const o = {
        Driver: r.name
      };
      for (const m of months) {
        o[`${monthLabel(m)} patients`] = r.months[m].patients;
        o[`${monthLabel(m)} items`] = r.months[m].items;
      }
      o["Total patients"] = r.totalPatients;
      o["Total items"] = r.totalItems;
      return o;
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(pSheet(data.patients.success)), "Delivered");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(pSheet(data.patients.failed)), "Failed");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(dSheet(data.drivers.success)), "Drivers delivered");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(dSheet(data.drivers.failed)), "Drivers failed");
    XLSX.writeFile(wb, `delivery_review_${(data.pharmacyName || "pharmacy").replace(/[^a-z0-9]+/gi, "_")}_${from}_to_${to}.xlsx`);
  };
  return <div>
      <div style={{
      display: "flex",
      flexWrap: "wrap",
      gap: 10,
      alignItems: "center",
      marginBottom: 10
    }}>
        <label style={{
        fontSize: 13
      }}>Pharmacy{" "}
          <select value={pharmacyId} onChange={e => setPharmacyId(e.target.value)} style={{
          padding: "5px 8px",
          minWidth: 240
        }}>
            <option value="">— choose a pharmacy —</option>
            {sortedList.map(p => <option key={p.pharmacyId} value={p.pharmacyId}>{maskName(p.pharmacyName, masked)}</option>)}
          </select>
        </label>
        <label style={{
        fontSize: 13
      }}>From <input type="date" value={from} max={to} onChange={e => setFrom(e.target.value)} /></label>
        <label style={{
        fontSize: 13
      }}>To <input type="date" value={to} min={from} onChange={e => setTo(e.target.value)} /></label>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search patient or postcode…" style={{
        padding: "6px 10px",
        fontSize: 13,
        border: "1px solid #D9D9D9",
        borderRadius: 6,
        width: 240
      }} />
        {search && <button style={btn(false)} onClick={() => setSearch("")}>Clear</button>}
        <span style={{
        flex: 1
      }} />
        {data && <button style={btn(false)} onClick={exportTab} title="All four tables in one workbook, with the search applied to the patient sheets">Export all to Excel</button>}
      </div>

      <div style={{
      display: "flex",
      gap: 6,
      marginBottom: 10,
      flexWrap: "wrap"
    }}>
        {[["patients", "Patients delivered to"], ["failed", "Failed deliveries"], ["drivers", "Drivers"], ["map", "Heat map"], ["efficiency", "Efficiency"], ["activity", "Activity"]].map(([id, label]) => <button key={id} style={btn(tab === id)} onClick={() => setTab(id)}>{label}
            {data && id === "patients" ? ` (${data.patients.success.length})` : ""}{data && id === "failed" ? ` (${data.patients.failed.length})` : ""}</button>)}
        {data && <span style={{
        fontSize: 12,
        color: "#5D5D5D",
        alignSelf: "center"
      }}>{num(data.logs)} delivery log entries · {monthLabel(months[0])} – {monthLabel(months[months.length - 1])}</span>}
      </div>

      {!pharmacyId && tab !== "efficiency" && tab !== "activity" && <div style={{
      color: "#5D5D5D",
      fontSize: 13
    }}>Choose a pharmacy and a date range — or open <b>Efficiency</b> to compare every branch.</div>}
      {busy && <div style={{
      color: "#5D5D5D",
      fontSize: 13
    }}>Loading…</div>}
      {error && <div role="alert" style={{
      color: "#A8071A",
      fontSize: 13
    }}>{error}</div>}

      {data && !busy && tab === "patients" && <>
        <div style={{
        fontSize: 12,
        color: "#5D5D5D",
        marginBottom: 6
      }}>Successful deliveries. <b>Visits</b> = different delivery rounds the patient was on; <b>Items</b> = prescriptions delivered.</div>
        <PatientTable rows={data.patients.success.filter(match)} totals={data.patientTotals.success} months={months} masked={masked} label="successful deliveries" exportInfo={exportInfo("Patients delivered to")} />
      </>}
      {data && !busy && tab === "failed" && <>
        <div style={{
        fontSize: 12,
        color: "#5D5D5D",
        marginBottom: 6
      }}>Unsuccessful delivery attempts. <b>Visits</b> = different delivery rounds with a failed attempt; <b>Items</b> = prescriptions not delivered.</div>
        <PatientTable rows={data.patients.failed.filter(match)} totals={data.patientTotals.failed} months={months} masked={masked} label="failed deliveries" exportInfo={exportInfo("Failed deliveries")} />
      </>}
      {data && !busy && tab === "drivers" && <>
        <DriverTable title="Delivered" rows={data.drivers.success} totals={data.driverTotals.success} months={months} masked={masked} exportInfo={exportInfo("Drivers")} />
        <DriverTable title="Failed deliveries" rows={data.drivers.failed} totals={data.driverTotals.failed} months={months} masked={masked} exportInfo={exportInfo("Drivers")} />
      </>}
      {data && !busy && tab === "map" && <>
        <div style={{
        display: "flex",
        gap: 6,
        marginBottom: 8
      }}>
          {[["success", "Delivered"], ["failed", "Failed"], ["both", "Both"]].map(([id, label]) => <button key={id} style={btn(mapKind === id)} onClick={() => setMapKind(id)}>{label}</button>)}
          {q && <span style={{
          fontSize: 12,
          color: "#5D5D5D",
          alignSelf: "center"
        }}>Filtered by “{search}”</span>}
        </div>
        <HeatMap points={data.points.filter(match)} kind={mapKind} exportInfo={exportInfo("Delivery locations")} masked={masked} />
      </>}
      {/* Activity: deliveries, mileage and time by site and driver — has its own site, driver and date filters. */}
      {tab === "activity" && <DeliveryActivity token={token || localStorage.getItem("authToken") || localStorage.getItem("token")} />}
      {tab === "efficiency" && !busy && from && to && <DeliveryEfficiency data={data} from={from} to={to} masked={masked} match={match} search={q ? search : ""} onPickPharmacy={r => {
      const hit = sortedList.find(p => String(p.pharmacyId) === String(r.dbName)) || sortedList.find(p => String(p.pharmacyName) === String(r.pharmacyName));
      setPharmacyId(hit ? hit.pharmacyId : r.dbName);
    }} />}
    </div>;
}
